"use client";
import { fetchApplicants } from "@/utils/api";
import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";

interface Application {
  _id: string;
  id: number;
  type: string;
  created_at: string;
  job: {
    title: string;
    location_name: string;
  } | null;
  jobseeker: {
    first_name: string;
    last_name: string;
    email: string;
    full_address: string;
    city: string;
    state: string;
    title: string;
  };
}

interface TableItem {
  id: string;
  name: string;
  location: string;
  appliedAt: string;
  jobTitle: string;
  email: string;
}

// Function to mask email addresses
const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');
  if (localPart.length <= 2) return email;

  const firstTwoChars = localPart.substring(0, 4);
  const maskedPart = '*'.repeat(Math.min(localPart.length - 2, 3)); // Show max 3 asterisks
  return `${firstTwoChars}${maskedPart}@${domain}`;
};

// Transform applications data to match the table structure
const transformApplicationsData = (applications: Application[]): TableItem[] => {
  return applications.map(app => ({
    id: app._id,
    name: `${app.jobseeker.first_name} ${app.jobseeker.last_name}`,
    location: app.jobseeker.full_address || `${app.jobseeker.city}, ${app.jobseeker.state}`,
    appliedAt: new Date(app.created_at).toLocaleDateString(),
    jobTitle: app.job?.title || 'No title',
    email: app.jobseeker.email,
  }));
};

export default function RecentApplicants() {
  const [applicants, setApplicants] = useState<TableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadApplicants = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchApplicants({
          limit: 5,
          sortBy: 'created_at',
          sortOrder: 'desc'
        });

        if (data.success && data.data?.applications && Array.isArray(data.data.applications)) {
          const transformedData = transformApplicationsData(data.data.applications);
          setApplicants(transformedData);
        } else {
          setError("Invalid data format received");
        }
      } catch (err) {
        console.error('Error fetching applicants:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch applicants');
      } finally {
        setLoading(false);
      }
    };

    loadApplicants();
  }, []);

  if (loading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
        <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Recent Applicants
            </h3>
          </div>
        </div>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Loading applicants...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
        <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Recent Applicants
            </h3>
          </div>
        </div>
        <div className="text-center py-8 text-red-500 dark:text-red-400">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Recent Applicants
          </h3>
        </div>

        <div className="flex items-center gap-3">
          <a 
            href="/applicants"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
          >
            See all
          </a>
        </div>
      </div>
      <div className="max-w-full overflow-x-auto">
        <Table>
          {/* Table Header */}
          <TableHeader className="border-gray-100 dark:border-gray-800 border-y">
            <TableRow>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Applicant
              </TableCell>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Location
              </TableCell>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Job Applied For
              </TableCell>
              <TableCell
                isHeader
                className="py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
              >
                Applied At
              </TableCell>
            </TableRow>
          </TableHeader>

          {/* Table Body */}
          <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
            {applicants.length > 0 ? (
              applicants.map((item) => (
                <TableRow key={item.id} className="">
                  <TableCell className="py-3">
                    <div>
                      <p className="font-medium text-gray-800 text-theme-sm dark:text-white/90">
                        {item.name}
                      </p>
                      <span className="text-gray-500 text-theme-xs dark:text-gray-400">
                        {maskEmail(item.email)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {item.location}
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {item.jobTitle}
                  </TableCell>
                  <TableCell className="py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                    {item.appliedAt}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <td colSpan={4} className="py-8 text-center text-gray-500 dark:text-gray-400">
                  No applicants found
                </td>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
} 